import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { qk } from "@/lib/query-keys";
import { toastAdvertencia, toastErrorApi, toastExito, toastInfo } from "@/lib/toast";
import {
  listarActualizacionesClavesSat,
  solicitarActualizacionDesdeSat,
  subirCatalogoClavesSat,
  type ActualizacionClavesSat,
} from "../services/claves-sat-admin-service";
import { clavesSatAdminKey } from "./use-claves-sat-admin";

const actualizacionesKey = ["admin-claves-sat", "actualizaciones"] as const;
const CADA_SEGUNDOS_MIENTRAS_CORRE = 3_000;

/**
 * Historial de actualizaciones del catálogo · mientras hay una en curso se consulta cada pocos segundos; cuando
 * termina se refrescan el listado del gestor y las consultas de claves del wizard, y se avisa el resultado.
 */
export function useActualizacionesClavesSat() {
  const qc = useQueryClient();
  const consulta = useQuery({
    queryKey: actualizacionesKey,
    queryFn: () => listarActualizacionesClavesSat(10),
    refetchInterval: (q) => (q.state.data?.enCurso ? CADA_SEGUNDOS_MIENTRAS_CORRE : false),
  });

  // Aviso único al pasar de «en curso» a terminada (no al cargar la página con el historial ya terminado).
  const estabaEnCurso = useRef(false);
  const enCurso = consulta.data?.enCurso ?? false;
  const ultima = consulta.data?.items[0];
  useEffect(() => {
    if (enCurso) {
      estabaEnCurso.current = true;
      return;
    }
    if (!estabaEnCurso.current || !ultima) return;
    estabaEnCurso.current = false;
    void qc.invalidateQueries({ queryKey: clavesSatAdminKey });
    void qc.invalidateQueries({ queryKey: qk.clavesSat });
    avisarResultado(ultima);
  }, [enCurso, ultima, qc]);

  return consulta;
}

function avisarResultado(a: ActualizacionClavesSat) {
  if (a.estado === "Fallida") {
    toastAdvertencia({ title: "No se pudo actualizar el catálogo", message: a.error ?? "Revisa el historial." });
  } else if (a.estado === "SinCambios") {
    toastInfo({ title: "Catálogo al día", message: "El archivo del SAT no trae cambios respecto al catálogo del portal." });
  } else {
    toastExito({
      title: "Catálogo actualizado",
      message: `${a.creadas} nuevas · ${a.actualizadas} actualizadas · ${a.descontinuadas} descontinuadas.`,
    });
  }
}

/** Arranca la actualización (descarga del SAT o archivo subido) y deja al historial consultando hasta que termine. */
function useIniciarActualizacion<TVariables>(mutationFn: (v: TVariables) => Promise<ActualizacionClavesSat>, mensaje: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      toastInfo({ title: "Actualización iniciada", message: mensaje });
      void qc.invalidateQueries({ queryKey: actualizacionesKey });
    },
    onError: toastErrorApi,
  });
}

export const useActualizarDesdeSat = () =>
  useIniciarActualizacion<void>(() => solicitarActualizacionDesdeSat(), "Descargando el catálogo del SAT · puede tardar un par de minutos.");

export const useSubirCatalogoClavesSat = () =>
  useIniciarActualizacion<File>((archivo) => subirCatalogoClavesSat(archivo), "Procesando el archivo · puede tardar un par de minutos.");
